// A weak ENERGY PERIMETER around the playable area — a faint cyan containment curtain at the level's
// bounding box that reads instantly as "edge of the deployment zone" without walling off the view. Four
// additive shader panels: a soft vertical fade (brightest at the ground), rising scan bands, faint
// verticals, a bright floor rim, and a gentle pulse. Purely visual (collision is already handled by nav).

import * as THREE from "three";

const VERT = /* glsl */ `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
`;
const FRAG = /* glsl */ `
  precision highp float;
  varying vec2 vUv;
  uniform vec3 uColor;
  uniform float uTime;
  void main(){
    float up = vUv.y;                                  // 0 ground .. 1 top
    float vFade = pow(1.0 - up, 1.6);                  // dense at the base, thins upward
    float scan = pow(0.5 + 0.5 * sin(up * 26.0 - uTime * 2.2), 3.0); // rising energy bands
    float grid = smoothstep(0.92, 1.0, abs(sin(vUv.x * 150.0))) * 0.15; // faint verticals
    float rim = smoothstep(0.07, 0.0, up) * 1.05;      // bright line where it meets the ground
    float top = smoothstep(0.06, 0.0, 1.0 - up) * 0.4; // faint upper edge
    float a = vFade * 0.26 + scan * vFade * 0.72 + grid + rim + top;
    a *= 0.82 + 0.18 * sin(uTime * 1.3);               // gentle pulse
    gl_FragColor = vec4(uColor * (1.15 + scan * 0.7), a);
  }
`;

export interface BBox { minX: number; maxX: number; minZ: number; maxZ: number; }

export class Perimeter {
  readonly group = new THREE.Group();
  private mat: THREE.ShaderMaterial;

  constructor(bbox: BBox, color: number, baseY = 120, height = 440, margin = 70) {
    this.mat = new THREE.ShaderMaterial({
      uniforms: { uColor: { value: new THREE.Color(color) }, uTime: { value: 0 } },
      vertexShader: VERT, fragmentShader: FRAG,
      transparent: true, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending, fog: false,
    });
    const x0 = bbox.minX - margin, x1 = bbox.maxX + margin;
    const z0 = bbox.minZ - margin, z1 = bbox.maxZ + margin;
    const cx = (x0 + x1) / 2, cz = (z0 + z1) / 2, cy = baseY + height / 2;
    const wallX = x1 - x0, wallZ = z1 - z0;
    // two walls spanning X (at z0/z1), two spanning Z (at x0/x1)
    for (const z of [z0, z1]) this.addWall(wallX, height, cx, cy, z, 0);
    for (const x of [x0, x1]) this.addWall(wallZ, height, x, cy, cz, Math.PI / 2);
  }

  private addWall(w: number, h: number, x: number, y: number, z: number, ry: number): void {
    const m = new THREE.Mesh(new THREE.PlaneGeometry(w, h), this.mat);
    m.position.set(x, y, z);
    m.rotation.y = ry;
    m.frustumCulled = false;
    this.group.add(m);
  }

  update(t: number): void { this.mat.uniforms.uTime.value = t; }

  dispose(): void {
    this.group.traverse((o) => { if (o instanceof THREE.Mesh) o.geometry.dispose(); });
    this.mat.dispose();
    this.group.clear();
  }
}
